import { DataCompletenessCard } from "../components/health/DataCompletenessCard.js";
import { KiteStatusCard } from "../components/health/KiteStatusCard.js";
import { PipelineTable } from "../components/health/PipelineTable.js";
import { ReviewQueueCard } from "../components/health/ReviewQueueCard.js";
import { PageLayout } from "../components/ui/PageLayout.js";
import { Section } from "../components/ui/Section.js";
import { formatNumber, istDate } from "../lib/format.js";
import { type PipelineHealth, useHealth } from "../lib/queries.js";
import { shortSha } from "../lib/shortSha.js";

/**
 * Only `ok` is OK. On a fresh deploy nothing has failed yet, so counting
 * failures alone put "Every pipeline reported OK." above a table of fourteen
 * rows reading "Never run"; a pipeline whose worker died mid-run sits at
 * `running` forever and would have read the same way. Both are the summary a
 * broken deploy must not print.
 */
export interface PipelineTally {
  failing: number;
  never: number;
  running: number;
  /** Switched off by configuration, so not a failure and not an outage. */
  disabled: number;
}

export function summarise(tally: PipelineTally, total: number): string {
  const parts: string[] = [];
  if (tally.failing > 0) parts.push(`${tally.failing} of ${total} failed`);
  if (tally.never > 0) parts.push(`${tally.never} have never run`);
  if (tally.running > 0) parts.push(`${tally.running} still running`);
  if (tally.disabled > 0) parts.push(`${tally.disabled} switched off`);
  return parts.length === 0 ? "Every pipeline reported OK." : `${parts.join(", ")}.`;
}

/**
 * The page you open when a number looks wrong.
 *
 * It publishes the classifier's measured precision and recall next to the
 * events it produces, because an unmeasured classifier is a rumour generator
 * (spec §5.4) and the honest place for that number is beside its output.
 */
export function HealthPage() {
  const query = useHealth();

  if (query.isPending) return <Shell>Reading pipeline state…</Shell>;
  if (query.isError) return <Shell>{(query.error as Error).message}</Shell>;
  if (!query.data) return <Shell>Nothing to report.</Shell>;

  const { build, pipelines, counts, kite, review, classifier, data } = query.data;
  const count = (status: PipelineHealth["status"]) =>
    pipelines.filter((pipeline) => pipeline.status === status).length;
  const tally: PipelineTally = {
    failing: count("failed"),
    never: count("never"),
    running: count("running"),
    disabled: count("disabled"),
  };

  return (
    <PageLayout
      title="Health"
      meta={
        <span className="flex items-baseline gap-4">
          {/* Which commit produced the numbers below. Without it, "the fix
              is merged and the page still looks wrong" has no cheap answer
              and gets diagnosed as a stale deploy by guesswork. */}
          <span title="The commit this build came from" className="tabular-nums">
            build {shortSha(build)}
          </span>
          <span>{summarise(tally, pipelines.length)}</span>
        </span>
      }
    >
      <PipelineTable pipelines={pipelines} />

      <DataCompletenessCard data={data} />

      <div className="grid gap-4 md:grid-cols-3">
        <ReviewQueueCard review={review} counts={counts} />
        <KiteStatusCard
          kite={kite}
          login={pipelines.find((pipeline) => pipeline.name === "kite-login")}
        />

        <Section title="Event classifier">
          <dl className="flex flex-col gap-1 text-sm text-text-muted">
            <div className="flex justify-between gap-4">
              <dt>Version</dt>
              <dd className="text-text">{classifier.version}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Precision</dt>
              <dd className="text-text tabular-nums">{formatNumber(classifier.precision, 3)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Recall</dt>
              <dd className="text-text tabular-nums">{formatNumber(classifier.recall, 3)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Evaluated</dt>
              <dd className="text-text">{istDate(classifier.evaluatedAt)}</dd>
            </div>
          </dl>
          {classifier.precision === null ? (
            <p className="text-xs text-warning">
              This version has never been evaluated. Treat every event it produced as unverified
              until the eval harness has run.
            </p>
          ) : null}
        </Section>
      </div>
    </PageLayout>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <PageLayout title="Health">
      <p className="text-text-muted">{children}</p>
    </PageLayout>
  );
}
